import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { showToast } from "../components/Toast";

const BASE_URL = "https://api.shubhamraj01.in";

export default function RestaurantDetailPage({ restaurant, setActivePage, openAuthModal }) {
  const { token, isLoggedIn } = useAuth();
  const { addToCart, cart }   = useCart();
  const [foods, setFoods]         = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");

  const FILTERS = ["All", "Veg", "Non-Veg", "Seasonal"];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (restaurant?.id) loadFoods(); }, [restaurant]);

  const loadFoods = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/food/restaurant/${restaurant.id}?vegetarian=false&seasonal=false&nonveg=false`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await res.json();
      setFoods(data);
      setFiltered(data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (activeFilter === "All")           setFiltered(foods);
    else if (activeFilter === "Veg")      setFiltered(foods.filter(f => f.vegetarian));
    else if (activeFilter === "Non-Veg")  setFiltered(foods.filter(f => !f.vegetarian));
    else if (activeFilter === "Seasonal") setFiltered(foods.filter(f => f.seasonal));
  }, [activeFilter, foods]);

  const handleAddToCart = (food) => {
    if (!isLoggedIn()) { openAuthModal("login"); return; }
    addToCart({ ...food, restaurantId: restaurant.id });
    showToast(food.name + " added to cart!");
  };

  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  if (!restaurant) return null;

  return (
    <div>
      {/* Hero */}
      <div style={{ position: "relative", height: "280px", background: "var(--surface2)" }}>
        {restaurant.images?.[0]
          ? <img src={restaurant.images[0]} alt={restaurant.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "80px" }}>{"🍽️"}</div>
        }
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(0,0,0,.75) 0%,transparent 50%)" }} />

        {/* Back button */}
        <button onClick={() => setActivePage("restaurants")} style={{
          position: "absolute", top: "1rem", left: "1.5rem",
          background: "rgba(255,255,255,.15)", backdropFilter: "blur(8px)",
          border: "1.5px solid rgba(255,255,255,.3)", color: "#fff",
          padding: ".4rem 1rem", borderRadius: "99px", cursor: "pointer",
          fontSize: ".85rem", fontWeight: 600,
        }}>Back</button>

        {/* Name */}
        <div style={{ position: "absolute", bottom: "1.5rem", left: "clamp(1rem,5vw,3rem)" }}>
          <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem,3vw,2rem)", color: "#fff", marginBottom: ".3rem" }}>
            {restaurant.name}
          </h2>
          <p style={{ color: "rgba(255,255,255,.75)", fontSize: ".9rem" }}>
            {restaurant.cuisineType} · {restaurant.openingHours}
          </p>
        </div>

        {/* Open/Closed */}
        <div style={{
          position: "absolute", top: "1rem", right: "1.5rem",
          background: restaurant.open ? "rgba(45,106,79,.9)" : "rgba(153,27,27,.9)",
          color: "#fff", fontSize: ".75rem", fontWeight: 700,
          padding: ".3rem .75rem", borderRadius: "99px",
        }}>
          {restaurant.open ? "OPEN" : "CLOSED"}
        </div>
      </div>

      {/* Body */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 340px", gap: "2rem",
        padding: "2rem clamp(1rem,5vw,3rem)", maxWidth: "1200px", margin: "0 auto",
        alignItems: "start",
      }}>

        {/* Left — Menu */}
        <div>
          {/* Filter chips */}
          <div style={{ display: "flex", gap: ".5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => setActiveFilter(f)} style={{
                padding: ".35rem .85rem", borderRadius: "99px", border: "1.5px solid",
                borderColor: activeFilter === f ? "var(--primary)" : "var(--border)",
                background: activeFilter === f ? "var(--primary)" : "var(--surface)",
                color: activeFilter === f ? "#fff" : "var(--text2)",
                fontSize: ".78rem", fontWeight: 600, cursor: "pointer", transition: "all .2s",
              }}>{f}</button>
            ))}
          </div>

          {/* Food items */}
          {loading ? (
            <div style={{ display: "flex", gap: ".5rem", justifyContent: "center", padding: "3rem" }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: "10px", height: "10px", borderRadius: "50%", background: "var(--primary)", animation: `blink ${.6 + i*.2}s ease-in-out infinite` }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem", color: "var(--text3)" }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{"🍽️"}</div>
              <p>No items found</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
              {filtered.map(food => (
                <div key={food.id} style={{
                  display: "flex", alignItems: "center", gap: "1rem",
                  background: "var(--surface)", border: "1.5px solid var(--border)",
                  borderRadius: "16px", padding: "1rem", transition: "all .2s",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--primary)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                >
                  {/* Image */}
                  <div style={{ width: "80px", height: "80px", borderRadius: "12px", overflow: "hidden", background: "var(--surface2)", flexShrink: 0 }}>
                    {food.images?.[0]
                      ? <img src={food.images[0]} alt={food.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" }}>{"🍱"}</div>
                    }
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginBottom: ".2rem" }}>
                      <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: food.vegetarian ? "var(--accent)" : "var(--primary)", flexShrink: 0 }} />
                      <h4 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: ".95rem" }}>{food.name}</h4>
                      {food.seasonal && (
                        <span style={{ fontSize: ".65rem", fontWeight: 600, background: "var(--gold-l)", color: "var(--gold)", padding: ".15rem .4rem", borderRadius: "99px" }}>Seasonal</span>
                      )}
                    </div>
                    <p style={{ fontSize: ".78rem", color: "var(--text3)", marginBottom: ".2rem" }}>{food.foodCategory?.name}</p>
                    <p style={{ fontSize: ".82rem", color: "var(--text2)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {food.description}
                    </p>
                  </div>

                  {/* Price + Add */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: ".5rem", flexShrink: 0 }}>
                    <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--primary)" }}>Rs.{food.price}</span>
                    <button onClick={() => handleAddToCart(food)} style={{
                      background: "var(--primary-l)", color: "var(--primary)",
                      border: "1.5px solid rgba(232,93,47,.3)", borderRadius: "10px",
                      width: "34px", height: "34px", fontSize: "1.1rem", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, transition: "all .2s",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = "var(--primary)"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "var(--primary-l)"; e.currentTarget.style.color = "var(--primary)"; }}
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — Order Summary */}
        <div style={{
          background: "var(--surface)", border: "1.5px solid var(--border)",
          borderRadius: "20px", padding: "1.5rem",
          position: "sticky", top: "calc(68px + 1rem)",
        }}>
          <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "1.1rem", marginBottom: "1.25rem" }}>
            Order Summary
          </h3>

          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem 1rem", color: "var(--text3)", fontSize: ".85rem" }}>
              Add items to start your order
            </div>
          ) : (
            <>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginBottom: "1rem" }}>
                {cart.map(item => (
                  <div key={item.id} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: ".6rem 0", borderBottom: "1px solid var(--border)", gap: ".5rem",
                  }}>
                    <span style={{ fontSize: ".85rem", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</span>
                    <span style={{ fontSize: ".85rem", color: "var(--text3)" }}>x{item.qty}</span>
                    <span style={{ fontSize: ".85rem", fontWeight: 600, color: "var(--primary)", flexShrink: 0 }}>Rs.{item.price * item.qty}</span>
                  </div>
                ))}
              </div>
              <div style={{ height: "1px", background: "var(--border)", margin: ".75rem 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem", marginBottom: ".4rem", color: "var(--text2)" }}>
                <span>Subtotal</span><span>Rs.{cartTotal}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem", marginBottom: ".4rem", color: "var(--text2)" }}>
                <span>Delivery fee</span><span>Rs.40</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1rem", paddingTop: ".5rem" }}>
                <span>Total</span><span>Rs.{cartTotal + 40}</span>
              </div>
              <button
                onClick={() => { if (!isLoggedIn()) { openAuthModal("login"); return; } setActivePage("checkout"); }}
                style={{
                  width: "100%", background: "var(--primary)", color: "#fff", border: "none",
                  padding: ".85rem", borderRadius: "12px", fontWeight: 700, fontSize: ".95rem",
                  cursor: "pointer", marginTop: "1rem", fontFamily: "inherit", transition: "all .2s",
                }}
              >
                Place Order
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}